import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "contentType",
    "partialName",
    "tracksFilters",
    "playlistsFilters",
    "performancesFilters",
    "handPickedSection",
    "trackType",
    "categoryId",
    "playlistType",
    "playlistCategoryId",
    "region",
    "presetsSection",
    "showPresetsButton",
    "examplesSection",
    "titleSection",
    "titleInput",
    "titleHint",
    "combinationError"
  ]

  static values = { presets: Array }

  validCombinations = {
    tracks: ['latests'],
    playlists: ['playlists', 'hand_picked'],
    performances: ['upcoming_performances'],
    bands: ['latests'],
    stages: ['latests'],
    labels: ['latests']
  }

  allPartialNames = [
    { value: 'latests', label: 'Latests' },
    { value: 'playlists', label: 'Playlists' },
    { value: 'hand_picked', label: 'Hand-picked' },
    { value: 'upcoming_performances', label: 'Upcoming Performances' }
  ]

  connect() {
    this.updateFilters()
    this.updatePartialNameOptions()
  }

  updatePartialNameOptions() {
    if (!this.hasPartialNameTarget) return

    const contentType = this.contentTypeTarget.value
    const validPartials = this.validCombinations[contentType] || []

    // Store current value before clearing options
    const currentValue = this.partialNameTarget.value

    // Clear existing options
    this.partialNameTarget.innerHTML = ''

    // Add valid partial names
    validPartials.forEach(partialValue => {
      const partial = this.allPartialNames.find(p => p.value === partialValue)
      if (partial) {
        const option = document.createElement('option')
        option.value = partial.value
        option.textContent = partial.label
        this.partialNameTarget.appendChild(option)
      }
    })

    // Restore value if it's in the valid options
    // Priority: preset partial name > current value > blank
    if (this.presetPartialName && validPartials.includes(this.presetPartialName)) {
      this.partialNameTarget.value = this.presetPartialName
      this.presetPartialName = null // Clear after use
    } else if (currentValue && validPartials.includes(currentValue)) {
      this.partialNameTarget.value = currentValue
    } else {
      this.partialNameTarget.value = ''
    }
  }

  validateCombination() {
    if (!this.hasCombinationErrorTarget) return

    const contentType = this.contentTypeTarget.value
    const partialName = this.partialNameTarget.value

    if (contentType && partialName) {
      const validPartials = this.validCombinations[contentType] || []
      if (!validPartials.includes(partialName)) {
        this.combinationErrorTarget.textContent = `Partial name "${partialName}" is not compatible with content type "${contentType}". Valid options: ${validPartials.join(', ')}`
        this.combinationErrorTarget.classList.remove('hidden')
      } else {
        this.combinationErrorTarget.textContent = ''
        this.combinationErrorTarget.classList.add('hidden')
      }
    } else {
      this.combinationErrorTarget.textContent = ''
      this.combinationErrorTarget.classList.add('hidden')
    }
  }

  showPresets() {
    this.presetsSectionTarget.classList.remove('hidden')
    this.showPresetsButtonTarget.classList.add('hidden')
  }

  hidePresets() {
    this.presetsSectionTarget.classList.add('hidden')
    this.showPresetsButtonTarget.classList.remove('hidden')
  }

  applyPreset(event) {
    const preset = JSON.parse(event.currentTarget.dataset.preset)

    // Find form fields
    const form = this.element.querySelector('form')
    const contentTypeField = form.querySelector('select[name="swimlane[content_type]"]')
    const partialNameField = form.querySelector('select[name="swimlane[partial_name]"]')
    const iconField = form.querySelector('input[name="swimlane[icon]"]')

    // Apply multilingual titles
    this.applyMultilingualPreset(preset)

    // Handle user playlists preset with custom title prompt
    if (preset.filters && preset.filters.type === 'user_created') {
      // For user playlists, prompt for title in multiple languages or just default language
      this.showMultilingualCustomTitlePrompt(preset)
    }

    if (contentTypeField && preset.content_type) contentTypeField.value = preset.content_type
    if (partialNameField && preset.partial_name) partialNameField.value = preset.partial_name
    if (iconField && preset.icon) iconField.value = preset.icon

    // Store the preset partial name to preserve it during update
    this.presetPartialName = preset.partial_name

    // Apply filters
    if (preset.filters) {
      // Set dropdown values based on filters
      if (preset.filters.track_type && this.hasTrackTypeTarget) {
        this.trackTypeTarget.value = preset.filters.track_type
      }
      if (preset.filters.type && this.hasPlaylistTypeTarget) {
        this.playlistTypeTarget.value = preset.filters.type
      }
      if (preset.filters.region && this.hasRegionTarget) {
        this.regionTarget.value = preset.filters.region
      }
      if (preset.filters.category_id) {
        if (this.hasCategoryIdTarget) {
          this.categoryIdTarget.value = preset.filters.category_id
        }
        if (this.hasPlaylistCategoryIdTarget) {
          this.playlistCategoryIdTarget.value = preset.filters.category_id
        }
      }
    }

    // Update UI to show correct filter section
    this.updateFilters()
    
    // Hide presets section and show presets button
    this.hidePresets()

    // Scroll to top of form
    this.element.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  showCustomTitlePrompt(titleField, defaultTitle) {
    // Create a simple prompt to ask for custom title
    const customTitle = prompt(
      'Please provide a custom title for this User Playlists swimlane (e.g., "My Favorite User Playlists", "Popular User Mixes"):',
      titleField && titleField.value ? titleField.value : defaultTitle
    )
    
    if (customTitle && customTitle.trim() !== '') {
      if (titleField) titleField.value = customTitle.trim()
    } else {
      // If user cancels or enters blank, use default but show a message
      if (titleField) titleField.value = defaultTitle
      alert('Using default title. You can change it in the Title field above.')
    }
  }

  applyMultilingualPreset(preset) {
    const form = this.element.querySelector('form')
    
    // Apply titles for each language if available in preset
    const languages = ['de', 'fr', 'it', 'en', 'rm']
    languages.forEach(lang => {
      const titleField = form.querySelector('input[name="swimlane[title_' + lang + ']"]')
      const presetTitle = preset['title_' + lang]
      if (titleField && presetTitle) {
        titleField.value = presetTitle
      }
    })
  }

  showMultilingualCustomTitlePrompt(preset) {
    const form = this.element.querySelector('form')
    
    // Get current titles from form
    const currentTitles = {}
    const languages = ['de', 'fr', 'it', 'en', 'rm']
    
    languages.forEach(lang => {
      const titleField = form.querySelector('input[name="swimlane[title_' + lang + ']"]')
      if (titleField) {
        currentTitles[lang] = titleField.value
      }
    })
    
    // Prompt for German title (primary language)
    const germanTitle = prompt(
      'Please provide a custom title for this User Playlists swimlane in German (primary language):',
      currentTitles['de'] || (preset && preset.title_de) || 'Benutzer erstellte Playlists'
    )
    
    if (germanTitle && germanTitle.trim() !== '') {
      // Set the German title
      const germanField = form.querySelector('input[name="swimlane[title_de]"]')
      if (germanField) germanField.value = germanTitle.trim()
      
      // Optionally prompt for other languages or auto-translate
      const shouldTranslateOthers = confirm('Would you like to provide titles in other languages? Click OK to enter titles for French, Italian, English, and Romansh, or Cancel to use the German title as default.')
      
      if (shouldTranslateOthers) {
        const translations = {
          'fr': 'Playlists créées par des utilisateurs',
          'it': 'Playlist create da utenti', 
          'en': 'User Created Playlists',
          'rm': 'Playlist create da utilisaders'
        }
        
        languages.forEach(lang => {
          if (lang !== 'de') {
            const titleField = form.querySelector('input[name="swimlane[title_' + lang + ']"]')
            if (titleField) {
              const translatedTitle = prompt(
                'Please provide a title in ' + lang.toUpperCase() + ':',
                translations[lang] || germanTitle
              )
              if (translatedTitle && translatedTitle.trim() !== '') {
                titleField.value = translatedTitle.trim()
              } else {
                titleField.value = germanTitle // Fallback to German
              }
            }
          }
        })
      } else {
        // Use German title for all languages
        languages.forEach(lang => {
          if (lang !== 'de') {
            const titleField = form.querySelector('input[name="swimlane[title_' + lang + ']"]')
            if (titleField) titleField.value = germanTitle
          }
        })
      }
    } else {
      // If user cancels, use preset titles
      this.applyMultilingualPreset(preset)
      alert('Using preset titles. You can customize them in the multilingual title fields above.')
    }
  }

  updateFilters() {
    const contentType = this.contentTypeTarget.value
    const partialName = this.partialNameTarget.value

    // Hide all filter sections
    this.tracksFiltersTarget.classList.add('hidden')
    this.playlistsFiltersTarget.classList.add('hidden')
    this.performancesFiltersTarget.classList.add('hidden')
    if (this.hasHandPickedSectionTarget) {
      this.handPickedSectionTarget.classList.add('hidden')
    }

    // Show relevant filter section based on content type and partial name
    switch(contentType) {
      case 'tracks':
      case 'bands':
      case 'stages':
      case 'labels':
        this.tracksFiltersTarget.classList.remove('hidden')
        break
      case 'playlists':
        this.playlistsFiltersTarget.classList.remove('hidden')
        break
      case 'performances':
        this.performancesFiltersTarget.classList.remove('hidden')
        break
    }

    this.updatePartialNameOptions()
    this.validateCombination()
    this.updateTitleHint()
    this.updateExamples()
    this.updateHandPickedVisibility()
  }

  updateExamplesForContentType(contentType) {
    const examples = this.getExamplesForContentType(contentType)
    if (examples.length === 0) return

    let examplesHtml = 
    `  <div class="mt-4">
        <h5 class="font-semibold text-gray-800 dark:text-gray-200 mb-2">✨ Examples for ${this.formatContentType(contentType)}</h5>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">`
    

    examples.forEach(example => {
      examplesHtml += 
       ` <div>
          <div class="text-gray-600 dark:text-gray-400 mb-1"><strong>${example.description}:</strong></div>
          <code class="block bg-gray-100 dark:bg-gray-800 p-2 rounded text-xs">${JSON.stringify(example.filters)}</code>
        </div>
      `
    })

    examplesHtml += '</div></div>'
    
    // Update the examples section in the DOM
    const examplesDiv = this.examplesSectionTarget.querySelector('[data-examples-content]')
    if (examplesDiv) {
      examplesDiv.innerHTML = examplesHtml
    } else {
      // Create the examples div if it doesn't exist
      const newExamplesDiv = document.createElement('div')
      newExamplesDiv.setAttribute('data-examples-content', '')
      newExamplesDiv.innerHTML = examplesHtml
      this.examplesSectionTarget.appendChild(newExamplesDiv)
    }
  }

  getExamplesForContentType(contentType) {
    const examples = {
      'tracks': [
        { description: "Latest tracks", filters: { track_type: "tracks" } },
        { description: "On air broadcasts", filters: { track_type: "broadcasts", featured: true } },
        { description: "Featured tracks in category", filters: { track_type: "tracks", category_id: 1, featured: true } }
      ],
      'playlists': [
        { description: "User-created playlists", filters: { type: "user_created" } },
        { description: "Radio curated playlists", filters: { type: "radio_curated" } },
        { description: "Band playlists", filters: { type: "band" } }
      ],
      'playlist_collections': [
        { description: "User playlist collections", filters: { type: "user_created" } },
        { description: "Radio curated collections", filters: { type: "radio_curated" } }
      ],
      'performances': [
        { description: "Central region performances", filters: { region: "central" } },
        { description: "Featured performances", filters: { featured: true } },
        { description: "All performances in region", filters: { region: "north" } }
      ],
      'bands': [
        { description: "Latest bands", filters: { track_type: "bands" } },
        { description: "Bands in category", filters: { track_type: "bands", category_id: 2 } },
        { description: "Featured bands", filters: { track_type: "bands", featured: true } }
      ],
      'stages': [
        { description: "Stage venues", filters: { track_type: "stages" } },
        { description: "Stages in category", filters: { track_type: "stages", category_id: 1 } }
      ],
      'labels': [
        { description: "Record labels", filters: { track_type: "labels" } },
        { description: "Labels in category", filters: { track_type: "labels", category_id: 3 } }
      ]
    }

    return examples[contentType] || []
  }

  formatContentType(contentType) {
    return contentType.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
  }

  updateExamples() {
    const contentType = this.contentTypeTarget.value
    if (this.hasExamplesSectionTarget) {
      this.updateExamplesForContentType(contentType)
    }
  }

  updateTitleHint() {
    if (!this.hasTitleHintTarget || !this.hasPlaylistTypeTarget) return

    const contentType = this.contentTypeTarget.value
    const playlistType = this.playlistTypeTarget.value

    // Show hint only for user-created playlists
    if (contentType === 'playlists' && playlistType === 'user_created') {
      this.titleHintTarget.style.display = 'block'
      // Optional: highlight the title field
      if (this.hasTitleInputTarget) {
        this.titleInputTarget.classList.add('border-primary-500', 'ring-2', 'ring-primary-500/20')
        this.titleInputTarget.focus()
      }
    } else {
      this.titleHintTarget.style.display = 'none'
      if (this.hasTitleInputTarget) {
        this.titleInputTarget.classList.remove('border-primary-500', 'ring-2', 'ring-primary-500/20')
      }
    }
  }

  validatePartialNameChange() {
    this.validateCombination()
    this.updateHandPickedVisibility()
  }

  updateHandPickedVisibility() {
    if (!this.hasHandPickedSectionTarget) return

    const contentType = this.contentTypeTarget.value
    const partialName = this.partialNameTarget.value

    // Show hand-picked section only when content type is playlists and partial is hand_picked
    if (contentType === 'playlists' && partialName === 'hand_picked') {
      this.handPickedSectionTarget.classList.remove('hidden')
      this.playlistsFiltersTarget.classList.add('hidden')
    } else {
      this.handPickedSectionTarget.classList.add('hidden')
      if (contentType === 'playlists') {
        this.playlistsFiltersTarget.classList.remove('hidden')
      }
    }
  }

  disconnect() {
    // Clean up when controller is disconnected (PJAX navigation, etc.)
    // This prevents stale references to old DOM elements after PJAX page updates
    this.element = null
  }
}
